/**
 * Design System - Input Component
 *
 * Text input component with icon support
 */

import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../utils/cn';

const inputVariants = cva(
  'w-full px-4 py-2 rounded-lg border bg-white text-[#1C1917] placeholder:text-[#78716C] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#FDE047] focus:border-transparent disabled:bg-[#F5F5F4] disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        default: 'border-[#D6D3D1]',
        error: 'border-red-500 focus:ring-red-500',
        success: 'border-green-500 focus:ring-green-500',
      },
      inputSize: {
        sm: 'h-8 text-sm px-3 py-1',
        md: 'h-10 text-sm px-4 py-2',
        lg: 'h-12 text-base px-4 py-3',
      },
    },
    defaultVariants: {
      variant: 'default',
      inputSize: 'md',
    },
  }
);

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {
  /**
   * Icon to be rendered before the input
   */
  leftIcon?: React.ReactNode;
  /**
   * Icon to be rendered after the input
   */
  rightIcon?: React.ReactNode;
  /**
   * Label for the input
   */
  label?: string;
  /**
   * Helper text below the input
   */
  helperText?: string;
  /**
   * Error message
   */
  error?: string;
  /**
   * Container class name
   */
  containerClassName?: string;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      containerClassName,
      variant,
      inputSize,
      leftIcon,
      rightIcon,
      label,
      helperText,
      error,
      ...props
    },
    ref
  ) => {
    const hasError = !!error;
    const finalVariant = hasError ? 'error' : variant;

    return (
      <div className={cn('flex flex-col gap-1', containerClassName)}>
        {label && (
          <label className='text-sm font-semibold text-[#1C1917]'>
            {label}
          </label>
        )}

        <div className='relative'>
          {leftIcon && (
            <div className='absolute left-3 top-1/2 -translate-y-1/2 text-[#78716C]'>
              {leftIcon}
            </div>
          )}

          <input
            className={cn(
              inputVariants({ variant: finalVariant, inputSize }),
              leftIcon && 'pl-10',
              rightIcon && 'pr-10',
              className
            )}
            ref={ref}
            {...props}
          />

          {rightIcon && (
            <div className='absolute right-3 top-1/2 -translate-y-1/2 text-[#78716C]'>
              {rightIcon}
            </div>
          )}
        </div>

        {(error || helperText) && (
          <p
            className={cn('text-xs', error ? 'text-red-600' : 'text-[#78716C]')}
          >
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
